import { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { getLang } from 'shared/store/selectors/lang.selector';
import { Table, SetFilter, setFilterDoesFilterPass } from 'src/components/design-system/table';
import styles from './reports.module.scss';

const PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export const SET_FILTER = { component: SetFilter, doesFilterPass: setFilterDoesFilterPass };

// debounceMs 0 so the Reset's enabled state updates on the first keystroke
// instead of after ag-grid's ~500ms filter debounce.
export const RESETTABLE_FILTER = { buttons: ['reset'], debounceMs: 0 };

export function ReportTable({ columnDefs, rowData, getRowId, onRowActivate }) {
  const lang = useSelector(getLang('EDUCATIONAL_EVENTS'));
  const openFilter = useRef({ colId: null, eGui: null });

  const handleCellClicked = params => {
    if (!onRowActivate) return;
    if (params.event?.target?.closest(`.${styles.linkCell}`)) return;
    if (params.data) onRowActivate(params.data);
  };

  // Native filter Reset is always enabled; mirror SetFilter by disabling it when
  // the open column has no active model.
  const syncResetDisabled = useCallback(api => {
    const { colId, eGui } = openFilter.current;
    const button = eGui?.querySelector('.ag-filter-apply-panel-button');
    if (!colId || !button) return;
    button.disabled = api.getColumnFilterModel(colId) == null;
  }, []);

  const onGridReady = useCallback(
    ({ api }) => {
      api.addEventListener('filterOpened', event => {
        openFilter.current = { colId: event.column?.getColId?.() ?? null, eGui: event.eGui };
        syncResetDisabled(api);
        requestAnimationFrame(() => syncResetDisabled(api));
      });
      api.addEventListener('filterChanged', () => syncResetDisabled(api));
    },
    [syncResetDisabled],
  );

  return (
    <Table
      rootClassName={styles.tableRoot}
      containerProps={{ className: styles.table }}
      columnDefs={columnDefs}
      rowData={rowData}
      getRowId={getRowId}
      onGridReady={onGridReady}
      onCellClicked={onRowActivate ? handleCellClicked : undefined}
      rowClass={onRowActivate ? styles.clickableRow : undefined}
      overlayNoRowsTemplate={lang.REPORT_EMPTY}
      enableActiveFilters
      pagination
      paginationPageSize={PAGE_SIZE}
      paginationPageSizeSelector={PAGE_SIZE_OPTIONS}
    />
  );
}
